(() => {
  const BASE = "https://cdn.shopify.com/s/files/1/0959/0531/7129/files/";
  const SKU_INDEX_URL = BASE + "sku-index.json";
  const STORAGE_KEY = "ridesrus_vehicle_v1";
  const SKU_INDEX_CACHE_KEY = "fitment_sku_index_product_v1";
  const SKU_INDEX_CACHE_MS = 60 * 60 * 1000;

  const STATE = {
    NO_VEHICLE: "no_vehicle",
    NEEDS_YEAR: "needs_year",
    FITS: "fits",
    NOT_FITS: "not_fits",
    ERROR: "error"
  };

  function normSku(s) {
    return String(s || "")
      .trim()
      .replace(/\s+/g, "")
      .toUpperCase();
  }

  function slug(s) {
    return (s || "")
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function loadVehicle() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  async function fetchJson(url) {
    const r = await fetch(url, { cache: "no-store" });

    if (!r.ok) {
      throw new Error(`Fetch failed (${r.status}) ${url}`);
    }

    return r.json();
  }

  async function fetchSkuIndex() {
    try {
      const raw = sessionStorage.getItem(SKU_INDEX_CACHE_KEY);

      if (raw) {
        const parsed = JSON.parse(raw);

        if (
          parsed &&
          parsed.t &&
          (Date.now() - parsed.t) < SKU_INDEX_CACHE_MS &&
          parsed.v
        ) {
          return parsed.v;
        }
      }
    } catch {}

    const index = await fetchJson(SKU_INDEX_URL);

    try {
      sessionStorage.setItem(
        SKU_INDEX_CACHE_KEY,
        JSON.stringify({
          t: Date.now(),
          v: index
        })
      );
    } catch {}

    return index;
  }

  function collectProductSkus(variantMap) {
    const skus = new Set();

    for (const sku of Object.values(variantMap || {})) {
      const normalized = normSku(sku);

      if (normalized) {
        skus.add(normalized);
      }
    }

    return skus;
  }

  function productIsFitmentEligible(variantMap, skuIndex) {
    const productSkus = collectProductSkus(variantMap);

    if (!productSkus.size) {
      return false;
    }

    const fitmentSkus = new Set();

    for (const sku of Object.keys(skuIndex || {})) {
      const normalized = normSku(sku);

      if (normalized) {
        fitmentSkus.add(normalized);
      }
    }

    for (const sku of productSkus) {
      if (fitmentSkus.has(sku)) {
        return true;
      }
    }

    return false;
  }

  function collectSkusExact(makeData, model, cc, year) {
    const modelNode = makeData?.models?.[model];

    if (!modelNode) {
      return new Set();
    }

    const ccNode = modelNode?.cc || {};
    const ccCandidates = [];

    if (cc && ccNode[cc]) {
      ccCandidates.push(cc);
    }

    if (ccNode["_any"]) {
      ccCandidates.push("_any");
    }

    const yearCandidates = [];

    if (year && year !== "0") {
      yearCandidates.push(year);
    }

    yearCandidates.push("0");

    const out = new Set();

    for (const c of ccCandidates) {
      const yNode = ccNode[c] || {};

      for (const y of yearCandidates) {
        const payload = yNode[y];

        if (payload?.skus && Array.isArray(payload.skus)) {
          payload.skus.forEach(s => out.add(normSku(s)));
        }
      }
    }

    return out;
  }

  function collectSkusAnyYear(makeData, model, cc) {
    const modelNode = makeData?.models?.[model];

    if (!modelNode) {
      return new Set();
    }

    const ccNode = modelNode?.cc || {};
    const ccCandidates = [];

    if (cc && ccNode[cc]) {
      ccCandidates.push(cc);
    }

    if (ccNode["_any"]) {
      ccCandidates.push("_any");
    }

    const out = new Set();

    for (const c of ccCandidates) {
      const yNode = ccNode[c] || {};

      for (const yKey of Object.keys(yNode)) {
        const payload = yNode[yKey];

        if (payload?.skus && Array.isArray(payload.skus)) {
          payload.skus.forEach(s => out.add(normSku(s)));
        }
      }
    }

    return out;
  }

  function getSelectedVariantId(productForm) {
    const idInput = productForm?.querySelector('input[name="id"]');
    return idInput ? idInput.value : "";
  }

  function resolveSkuFromVariantId(map, variantId) {
    if (!map || !variantId) {
      return "";
    }

    return map[variantId] || "";
  }

  function setBadge(badge, state, vehicleText, sku) {
    const elState = badge.querySelector("[data-fitment-state]");
    const elMsg = badge.querySelector("[data-fitment-message]");
    const elSub = badge.querySelector("[data-fitment-sub]");
    const elVehicle = badge.querySelector("[data-fitment-vehicle]");
    const elSku = badge.querySelector("[data-fitment-sku]");

    badge.setAttribute("data-state", state);

    elVehicle.textContent = vehicleText || "";
    elSku.textContent = sku || "";

    elSub.textContent = "";
    elSub.style.display = "block";

    if (state === STATE.NO_VEHICLE) {
      elState.textContent = "Select your bike";
      elMsg.textContent = "Choose your bike to check fitment for this part.";
      elSub.textContent = "Tip: pick Make + Model first (Year/CC optional).";
      return;
    }

    if (state === STATE.NEEDS_YEAR) {
      elState.textContent = "Almost there";
      elMsg.textContent = "This part may fit — select a Year to confirm.";
      elSub.textContent = "Year makes fitment checks precise.";
      return;
    }

    if (state === STATE.FITS) {
      elState.textContent = "Fits your bike";
      elMsg.textContent =
        "This selected variant is compatible with your current bike selection.";
      elSub.textContent = "";
      elSub.style.display = "none";
      return;
    }

    if (state === STATE.NOT_FITS) {
      elState.textContent = "Doesn't fit your bike";
      elMsg.textContent =
        "This selected variant isn't listed for your current bike selection.";
      elSub.textContent = "Try changing Year/CC or your bike selection.";
      return;
    }

    elState.textContent = "Fitment unavailable";
    elMsg.textContent = "We couldn't check fitment right now.";
    elSub.textContent = "";
  }

  function formatVehicle(v) {
    if (!v) {
      return "";
    }

    const bits = [];

    if (v.year && v.year !== "0") {
      bits.push(v.year);
    }

    if (v.make) {
      bits.push(v.make);
    }

    if (v.model) {
      bits.push(v.model);
    }

    if (v.cc && v.cc !== "_any") {
      bits.push(`${v.cc}cc`);
    }

    return bits.join(" ");
  }

  async function evaluateFitment({
    badge,
    productForm,
    variantMap
  }) {
    const vehicle = loadVehicle();
    const variantId = getSelectedVariantId(productForm);
    const sku = resolveSkuFromVariantId(variantMap, variantId);
    const normalizedSku = normSku(sku);
    const vehicleText = formatVehicle(vehicle);

    if (!vehicle?.make || !vehicle?.model) {
      setBadge(badge, STATE.NO_VEHICLE, "", sku);
      return;
    }

    if (!normalizedSku) {
      setBadge(badge, STATE.ERROR, vehicleText, "");
      return;
    }

    const makeFile = BASE + slug(vehicle.make) + ".json";

    try {
      const makeData = await fetchJson(makeFile);

      if (!vehicle.year || vehicle.year === "0") {
        const anyYearSkus = collectSkusAnyYear(
          makeData,
          vehicle.model,
          vehicle.cc || "_any"
        );

        const match = anyYearSkus.has(normalizedSku);

        setBadge(
          badge,
          match ? STATE.NEEDS_YEAR : STATE.NOT_FITS,
          vehicleText,
          sku
        );

        return;
      }

      const exactSkus = collectSkusExact(
        makeData,
        vehicle.model,
        vehicle.cc || "_any",
        vehicle.year
      );

      const match = exactSkus.has(normalizedSku);

      setBadge(
        badge,
        match ? STATE.FITS : STATE.NOT_FITS,
        vehicleText,
        sku
      );
    } catch (e) {
      console.error("Product fitment check failed:", e);
      setBadge(badge, STATE.ERROR, vehicleText, sku);
    }
  }

  function watchVariantChanges({
    badge,
    productForm,
    variantMap
  }) {
    const idInput = productForm.querySelector('input[name="id"]');

    if (!idInput) {
      return;
    }

    const obs = new MutationObserver(() => {
      evaluateFitment({
        badge,
        productForm,
        variantMap
      });
    });

    obs.observe(idInput, {
      attributes: true,
      attributeFilter: ["value"]
    });

    productForm.addEventListener("change", () => {
      evaluateFitment({
        badge,
        productForm,
        variantMap
      });
    });

    window.addEventListener("vehicle:changed", () => {
      evaluateFitment({
        badge,
        productForm,
        variantMap
      });
    });
  }

  async function initOnPage() {
    const container = document.querySelector(
      "[data-fitment-product-container]"
    );

    const badge = document.querySelector(
      "[data-fitment-product-badge]"
    );

    const productForm =
      document.querySelector("product-form form") ||
      document.querySelector('form[action^="/cart/add"]');

    if (!container || !badge || !productForm) {
      return;
    }

    /*
      Safety rule:
      The badge starts hidden in Liquid and stays hidden unless
      we positively establish that this product contains at least
      one SKU present in the authoritative fitment SKU index.
    */
    container.hidden = true;

    let variantMap = {};

    try {
      variantMap = JSON.parse(
        badge.getAttribute("data-variant-map") || "{}"
      );
    } catch (e) {
      console.error("Could not parse product variant map:", e);
      return;
    }

    try {
      const skuIndex = await fetchSkuIndex();

      if (!productIsFitmentEligible(variantMap, skuIndex)) {
        return;
      }
    } catch (e) {
      /*
        Fail closed:
        if the eligibility index cannot be loaded, do not show
        a potentially irrelevant fitment badge.
      */
      console.error("Could not establish product fitment eligibility:", e);
      return;
    }

    container.hidden = false;

    await evaluateFitment({
      badge,
      productForm,
      variantMap
    });

    watchVariantChanges({
      badge,
      productForm,
      variantMap
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    initOnPage().catch(e => {
      console.error("Product fitment initialization failed:", e);
    });
  });
})();